// Mondes (thèmes visuels) — métadonnée COSMÉTIQUE d'un niveau, JAMAIS dans la simulation
// (le moteur, les scores, le hash et le rejeu ne connaissent pas le monde). Le monde décide,
// au RENDU seulement, du fond (dégradé + motif) et du jeu de tuiles structurelles (brique +
// béton) ; l'échelle, la corde, la glace, le sable, l'or et les entités gardent leur aspect.
// Module PARTAGÉ (client + serveur) : le serveur valide/porte le monde, le client dessine.
// Aucune dépendance DOM ni moteur.

export type WorldKey = 'classique' | 'grotte' | 'prairie' | 'desert' | 'neige' | 'usine' | 'abysses' | 'manoir'

/** Monde par défaut : rend EXACTEMENT le look historique (cartes existantes inchangées). */
export const DEFAULT_WORLD: WorldKey = 'classique'

export interface WorldTheme {
  /** Libellé français affiché dans l'éditeur. */
  name: string
  /** Dégradé de fond, du HAUT vers le BAS (deux couleurs identiques = fond uni). */
  sky: [string, string]
  /** Motif décoratif STATIQUE du fond (dessiné + mis en cache par render.ts). Les décors
   *  ANIMÉS (flocons, néons, halo de soleil) sont ajoutés par-dessus, au tick de simulation. */
  motif: 'none' | 'stalactites' | 'clouds' | 'dunes' | 'sapins' | 'neons' | 'recif' | 'manoir'
  /** Couleur principale du motif (les teintes secondaires sont fixées dans render.ts). */
  motifColor: string
}

// Ordre = ordre d'affichage dans l'éditeur ; `classique` d'abord (défaut). Les palettes de
// TUILES vivent dans scripts/gen-sprites.mjs (générées dans le sheet) ; ici, seul le FOND.
export const WORLDS: Record<WorldKey, WorldTheme> = {
  classique: { name: 'Classique', sky: ['#100c22', '#0a0714'], motif: 'none', motifColor: '#0d0b1a' },
  grotte: { name: 'Grotte', sky: ['#0a0a12', '#15121f'], motif: 'stalactites', motifColor: '#2c2440' },
  prairie: { name: 'Prairie', sky: ['#69b3e8', '#cdeafb'], motif: 'clouds', motifColor: '#8fc24a' },
  // Désert : ciel chaud sable→ambre, dunes en silhouette + soleil (halo pulsé au tick).
  desert: { name: 'Désert', sky: ['#e79a3a', '#f7d89b'], motif: 'dunes', motifColor: '#caa062' },
  // Neige : ciel froid ardoise→givre, sapins + collines enneigées ; flocons animés au tick.
  neige: { name: 'Neige', sky: ['#6b83a6', '#cfe0ec'], motif: 'sapins', motifColor: '#ffffff' },
  // Usine néon : nuit industrielle, tuyaux + enseignes néon qui pulsent au tick.
  usine: { name: 'Usine néon', sky: ['#0a0d15', '#161c2a'], motif: 'neons', motifColor: '#2b3242' },
  // Abysses (16/07 nuit) : on est SOUS l'eau. La lumière tombe de la surface (haut clair) et meurt
  // dans le noir des grands fonds (bas). Récif + épave en silhouette ; bancs de poissons, méduses et
  // bulles animés au tick. Le monde des tuiles eau / source de bulles / souffle.
  abysses: { name: 'Abysses', sky: ['#1a7fa8', '#02101c'], motif: 'recif', motifColor: '#0b3d4f' },
  // Manoir hanté (16/07 nuit) : nuit d'orage. Pleine lune, manoir aux fenêtres allumées, arbres
  // morts et pierres tombales ; chauves-souris, brume et éclairs animés au tick.
  manoir: { name: 'Manoir hanté', sky: ['#2a1f3d', '#0b0810'], motif: 'manoir', motifColor: '#161027' },
}

/** Clés des mondes dans l'ordre de déclaration (éditeur, itérations). */
export const WORLD_KEYS = Object.keys(WORLDS) as WorldKey[]

/** Garde de type : `x` est-il une clé de monde connue ? (frontières API/protocole/catalogue) */
export function isWorldKey(x: unknown): x is WorldKey {
  return typeof x === 'string' && Object.prototype.hasOwnProperty.call(WORLDS, x)
}

/**
 * Normalise une valeur quelconque en WorldKey. Toute valeur inconnue ou absente retombe sur
 * le monde par défaut : jamais de fond ni de tuile manquants, quel que soit l'entrant (base
 * ancienne, message réseau, carte importée).
 */
export function toWorld(x: unknown): WorldKey {
  return isWorldKey(x) ? x : DEFAULT_WORLD
}
